import '../styles.css';
import heroImage from '../assets/Sample/adrien-bruneau-8cpR_Yf0rQs-unsplash.jpg';
import emptyProfile from '../assets/empty_profile.png';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { updateUserProfile } from '../api/auth';
import { isAllowedImageType } from '../utils/imageResize';
import {
    PROFILE_MAX_EDGE,
    PROFILE_QUALITY,
    prepareImage,
    uploadBlob,
} from '../utils/imageUpload';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteAccountModal from '../components/DeleteAccountModal';
import ChangePasswordModal from '../components/ChangePasswordModal';

// Pronouns UI maps onto User.gender (GENDER_CHOICES).
const PRONOUN_OPTIONS = [
    { value: 'female', label: 'she/her' },
    { value: 'male', label: 'he/him' },
    { value: 'other', label: 'they/them' },
    { value: 'prefer_not_to_say', label: 'prefer not to say' },
];

const pronounLabel = (value) =>
    PRONOUN_OPTIONS.find((option) => option.value === value)?.label || '';


function getEditProfileLayoutVariables() {
    const viewportWidth =
        typeof window === 'undefined' ? 1280 : window.innerWidth;
    const rem = (min, preferredPixels, max) =>
        `${Math.min(max, Math.max(min, preferredPixels / 16)).toFixed(3)}rem`;

    // This page scales as a single unit with viewport WIDTH (like MyPage's
    // fit-to-width), so hero / avatar / inputs keep the design's proportions
    // at any window aspect ratio. Vertical values therefore use vw too:
    // vw_fraction = (Figma_px × 0.9 / 1117) × (1117 / 1728) = Figma_px × 0.9 / 1728,
    // i.e. the same value the reference viewport produced, now width-driven.
    return {
        // 321 × 0.9 / 1728 = 0.1672 (hero banner height, full-bleed width)
        '--ep-hero-height': rem(10, viewportWidth * 0.1672, 24),
        '--ep-avatar-size': rem(6, viewportWidth * 0.1115, 14),
        '--ep-column-gap': rem(2, viewportWidth * 0.0833, 12),
        '--ep-form-width': rem(15, viewportWidth * 0.223, 28),
        '--ep-title-size': rem(1.4, viewportWidth * 0.0219, 2.75),
        '--ep-label-size': rem(0.85, viewportWidth * 0.0146, 1.4),
        '--ep-control-font': rem(0.8, viewportWidth * 0.0104, 1.2),
        '--ep-input-padding': rem(0.65, viewportWidth * 0.0104, 1.2),
        // 67 × 0.9 / 1728 = 0.0349 — save button matches input height (circle)
        '--ep-action-size': rem(2.2, viewportWidth * 0.0349, 4),
        '--ep-action-gap': rem(0.5, viewportWidth * 0.0093, 1.5),
        '--ep-form-top-offset': rem(0.5, viewportWidth * 0.0758, 8),
        '--ep-title-top-gap': rem(0.5, viewportWidth * 0.0073, 1.5),
        '--ep-label-gap': rem(0.5, viewportWidth * 0.0177, 1.5),
        '--ep-field-gap': rem(1, viewportWidth * 0.0291, 3),
        // 67 × 0.9 / 1728 = 0.0349 (input h), 111 × 0.9 / 1728 = 0.0578 (textarea)
        '--ep-input-height': rem(2.8, viewportWidth * 0.0349, 5),
        '--ep-textarea-height': rem(4, viewportWidth * 0.0578, 8),
        '--ep-delete-gap': rem(1.5, viewportWidth * 0.0453, 4),
    };
}

export default function EditProfile() {
    const { user, isLoadingUser, logout } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [editProfileLayoutVariables] = useState(
        getEditProfileLayoutVariables,
    );
    const [formData, setFormData] = useState({
        gender: '',
        username: '',
        bio: '',
    });
    // Per-field "just saved" flag → the button shows brown while the field
    // still matches what was saved; it goes grey again the moment it changes.
    const [savedFlags, setSavedFlags] = useState({
        gender: false,
        username: false,
        bio: false,
    });
    const [pronounsOpen, setPronounsOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);

    const fileInputRef = useRef(null);
    // The picked-and-resized blob waits here until the user clicks save, so
    // nothing reaches R2 for a photo they abandon.
    const [photoDraft, setPhotoDraft] = useState(null); // { blob, previewUrl }
    const [photoStatus, setPhotoStatus] = useState('');
    const [photoBusy, setPhotoBusy] = useState(false);

    // A blob: URL is a document-lifetime handle; without this the page leaks
    // one per pick. This single cleanup covers all three cases -- replacing the
    // pick, a successful save, and unmount -- because the cleanup runs with the
    // PREVIOUS draft whenever photoDraft changes. Do not also revoke by hand in
    // the handlers: that would revoke the same URL twice and obscure ownership.
    useEffect(
        () => () => {
            if (photoDraft) URL.revokeObjectURL(photoDraft.previewUrl);
        },
        [photoDraft],
    );

    // Hydrate the editable fields from the logged-in account once it loads.
    useEffect(() => {
        if (!user) return;
        setFormData((prev) => ({
            ...prev,
            username: user.username || '',
            bio: user.bio || '',
            gender: user.gender || '',
        }));
    }, [user]);

    const saveMutation = useMutation({
        mutationFn: ({ field, value }) => updateUserProfile({ [field]: value }),
        onSuccess: (data, variables) => {
            queryClient.setQueryData(['user'], data);
            // Mark the field as saved → button turns brown.
            setSavedFlags((prev) => ({ ...prev, [variables.field]: true }));
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Any edit clears the saved state → button back to grey.
        setSavedFlags((prev) => ({ ...prev, [name]: false }));
    };

    const savedValue = (field) => {
        if (field === 'gender') return user?.gender || '';
        if (field === 'username') return user?.username || '';
        if (field === 'bio') return user?.bio || '';
        return '';
    };

    const isDirty = (field) => formData[field] !== savedValue(field);

    // Grey button = there are unsaved changes (or nothing saved yet). Clicking
    // it persists the field to /api/users/me/ and turns it brown (= saved).
    const handleSave = (field) => {
        if (!isDirty(field)) return;
        saveMutation.mutate({ field, value: formData[field] });
    };

    const handlePhotoPick = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ''; // let the same file be picked again after a failure
        if (!file) return;

        if (!isAllowedImageType(file.type)) {
            setPhotoStatus(
                'Unsupported format. Use JPEG, PNG, or WebP. ' +
                    '(iPhone HEIC files are not supported.)',
            );
            return;
        }

        try {
            const blob = await prepareImage(file, PROFILE_MAX_EDGE, PROFILE_QUALITY);
            // The effect above revokes the previous preview URL for us.
            setPhotoDraft({ blob, previewUrl: URL.createObjectURL(blob) });
            setPhotoStatus('');
        } catch (error) {
            console.error('[profile-photo]', error);
            setPhotoStatus('Could not read that image.');
        }
    };

    // Deliberately NOT saveMutation: that one object is shared by gender,
    // username and bio, so its isPending would disable every other field's save
    // button for the length of the R2 PUT -- and it has no onError, so none of
    // the messages below would ever reach the screen.
    const handlePhotoSave = async () => {
        if (!photoDraft || photoBusy) return;
        setPhotoBusy(true);
        setPhotoStatus('Uploading…');
        try {
            // Always restart from the blob: a temp URL is consumed by promotion
            // and its presign expires after 900s, so reusing one makes the
            // second attempt fail in a stranger way than the first.
            const tempUrl = await uploadBlob(photoDraft.blob);
            const data = await updateUserProfile({ profile_photo: tempUrl });
            queryClient.setQueryData(['user'], data);
            // Clearing the draft triggers the cleanup effect, which revokes the
            // preview URL; the avatar falls back to the fresh server value.
            setPhotoDraft(null);
            setPhotoStatus('');
        } catch (error) {
            console.error('[profile-photo]', error);
            // The draft stays: the blob is still good, so retry is one click.
            if (error.response?.status === 429) {
                setPhotoStatus('Too many uploads. Try again in a minute.');
            } else if (error.response) {
                setPhotoStatus('Could not save the photo. Please try again.');
            } else {
                setPhotoStatus('Upload failed. Please try again.');
            }
        } finally {
            setPhotoBusy(false);
        }
    };

    const handleDeleted = () => {
        setDeleteModalOpen(false);
        logout();
        navigate('/');
    };

    const selectPronoun = (value) => {
        setFormData((prev) => ({ ...prev, gender: value }));
        setSavedFlags((prev) => ({ ...prev, gender: false }));
        setPronounsOpen(false);
    };

    if (isLoadingUser) return <LoadingSpinner />;

    // Fall back to the design's placeholder text when nobody is logged in,
    // so the page still renders like the mock instead of collapsing.
    const email = user?.email || 'email@gmail.com';
    const fullName =
        [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
        user?.username ||
        'full name';
    const role =
        Array.isArray(user?.role) && user.role.length
            ? user.role.join(', ')
            : 'volunteer/ internship';

    const saveButton = (field, label) => {
        const dirty = isDirty(field);
        // Brown while unchanged since last successful save.
        const saved = savedFlags[field] && !dirty;

        // Default: no button. Appears grey when the value differs from saved,
        // then turns brown after a successful click-to-save.
        if (!dirty && !saved) return null;

        return (
            <button
                type="button"
                className={`edit-profile-save${saved ? ' is-saved' : ''}`}
                aria-label={`Save ${label}`}
                disabled={!dirty || saveMutation.isPending}
                onClick={() => handleSave(field)}
            />
        );
    };

    return (
        <div className="edit-profile-page" style={editProfileLayoutVariables}>
            <section className="edit-profile-hero" aria-hidden="true">
                <img src={heroImage} alt="" />
            </section>

            <div className="edit-profile-layout">
                <aside className="edit-profile-sidebar">
                    <button
                        type="button"
                        className="edit-profile-avatar-button"
                        aria-label="Change profile photo"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <img
                            src={
                                photoDraft?.previewUrl ||
                                user?.profile_photo ||
                                emptyProfile
                            }
                            alt=""
                            className="edit-profile-avatar"
                        />
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        hidden
                        onChange={handlePhotoPick}
                    />
                    {photoDraft && (
                        <button
                            type="button"
                            className="edit-profile-save edit-profile-save-avatar"
                            aria-label="Save profile photo"
                            disabled={photoBusy}
                            onClick={handlePhotoSave}
                        />
                    )}
                    {photoStatus && (
                        <p className="edit-profile-avatar-status" aria-live="polite">
                            {photoStatus}
                        </p>
                    )}
                    <h1 className="edit-profile-title">{fullName}</h1>
                </aside>

                <form
                    className="edit-profile-form"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <div className="edit-profile-field edit-profile-field-static">
                        <span className="edit-profile-label">Email</span>
                        <p className="edit-profile-static-value">{email}</p>
                    </div>

                    <div className="edit-profile-field edit-profile-field-static">
                        <span className="edit-profile-label">Full Name</span>
                        <p className="edit-profile-static-value">{fullName}</p>
                    </div>

                    <div className="edit-profile-field">
                        <label
                            className="edit-profile-label"
                            htmlFor="edit-profile-pronouns"
                        >
                            Pronouns
                        </label>
                        <div className="edit-profile-control-wrap">
                            <div className="edit-profile-select">
                                <input
                                    id="edit-profile-pronouns"
                                    name="gender"
                                    type="text"
                                    value={pronounLabel(formData.gender)}
                                    readOnly
                                    onClick={() =>
                                        setPronounsOpen((open) => !open)
                                    }
                                    className="edit-profile-input edit-profile-input-toggle"
                                    placeholder="add your pronouns"
                                />
                                <span
                                    className={`edit-profile-caret${
                                        pronounsOpen ? ' is-open' : ''
                                    }`}
                                    aria-hidden="true"
                                />
                                {pronounsOpen && (
                                    <ul
                                        className="edit-profile-dropdown"
                                        role="listbox"
                                    >
                                        {PRONOUN_OPTIONS.map((option) => (
                                            <li
                                                key={option.value}
                                                role="option"
                                                aria-selected={
                                                    formData.gender ===
                                                    option.value
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className="edit-profile-dropdown-item"
                                                    onClick={() =>
                                                        selectPronoun(
                                                            option.value,
                                                        )
                                                    }
                                                >
                                                    {option.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {saveButton('gender', 'pronouns')}
                        </div>
                    </div>

                    <div className="edit-profile-field">
                        <label
                            className="edit-profile-label"
                            htmlFor="edit-profile-username"
                        >
                            Username
                        </label>
                        <div className="edit-profile-control-wrap">
                            <input
                                id="edit-profile-username"
                                name="username"
                                type="text"
                                value={formData.username}
                                onChange={handleChange}
                                className="edit-profile-input"
                                placeholder="username"
                            />
                            {saveButton('username', 'username')}
                        </div>
                    </div>

                    <div className="edit-profile-field">
                        <button
                            type="button"
                            className="edit-profile-label edit-profile-text-link"
                            onClick={() => setPasswordModalOpen(true)}
                        >
                            Change password
                        </button>
                    </div>

                    <div className="edit-profile-field">
                        <label
                            className="edit-profile-label"
                            htmlFor="edit-profile-bio"
                        >
                            Short Bio
                        </label>
                        <div className="edit-profile-control-wrap edit-profile-control-wrap-textarea">
                            <textarea
                                id="edit-profile-bio"
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                className="edit-profile-textarea"
                                placeholder="Tell us a little about yourself, your role, interests, and what you're passionate about. (2–4 sentences)"
                            />
                            {saveButton('bio', 'short bio')}
                        </div>
                    </div>

                    <div className="edit-profile-field">
                        <span className="edit-profile-label">Role</span>
                        <p className="edit-profile-static-value">{role}</p>
                    </div>

                    <div className="edit-profile-field">
                        <Link
                            to="/mypage/service-history"
                            className="edit-profile-label edit-profile-text-link"
                        >
                            History &amp; Upcoming
                        </Link>
                    </div>

                    <button
                        type="button"
                        className="edit-profile-delete"
                        onClick={() => setDeleteModalOpen(true)}
                    >
                        Delete Account
                    </button>
                </form>
            </div>

            <div className="edit-profile-bottom-lines" aria-hidden="true">
                <span className="edit-profile-bottom-line" />
                <span className="edit-profile-bottom-line" />
            </div>

            <ChangePasswordModal
                open={passwordModalOpen}
                onClose={() => setPasswordModalOpen(false)}
            />

            <DeleteAccountModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDeleted={handleDeleted}
                hasUsablePassword={user?.has_usable_password}
            />
        </div>
    );
}
